import { all, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';
import {
  CHANGE_CUTOUTS, CHANGE_SINGLE_CUTS, CHANGE_DOUBLE_CUTS, CHANGE_TRIPLE_CUTS, CHANGE_ROUNDED_CORNERS, CHANGE_INVERTED_CORNERS, CHANGE_TAP_HOLES, CHANGE_WALL_PANEL_ROUND_CUTS_SMALL, CHANGE_WALL_PANEL_ROUND_CUTS_BIG, CHANGE_WALL_PANEL_SQUARE_CUTS, CHANGE_WALL_PANEL_SINGLE_CUTS, CHANGE_WALL_PANEL_DOUBLE_CUTS, CHANGE_WALL_PANEL_TRIPLE_CUTS,
  CHANGE_FREESTANDING_HOBS, CHANGE_KITCHEN_ISLAND_SINGLE_CUTS, CHANGE_KITCHEN_ISLAND_ROUNDED_CORNERS, CHANGE_KITCHEN_ISLAND_SQUARE_CUTS,
  SELECT_MATERIAL, SELECT_KITCHEN_ISLAND_MATERIAL, SELECT_WALL_PANEL_MATERIAL,
  SET_DOUBLE_SIDED, SET_KITCHEN_ISLAND_DOUBLE_SIDED,
  SELECT_EXPRESSION, SELECT_KITCHEN_ISLAND_EXPRESSION, SELECT_WALL_PANEL_EXPRESSION,
  SELECT_EDGE, SELECT_KITCHEN_ISLAND_EDGE,
  SELECT_WALL_LOCATION, SELECT_KITCHEN_ISLAND_WALL_LOCATION,
  SELECT_THICKNESS, SELECT_KITCHEN_ISLAND_THICKNESS,
  SELECT_OVERHANG, SELECT_KITCHEN_ISLAND_OVERHANG,
  SELECT_OVERHANG_SIDE, SELECT_KITCHEN_ISLAND_OVERHANG_SIDE,
  SELECT_SINK_MODEL, SELECT_SINK_MATERIAL, SELECT_SINK_FASTENING, SELECT_DRAINER_GROOVES,
  CHANGE_SHAPE, SELECT_SPLASHBACK, SELECT_WALL_PANEL_MATCH_WORKTOP, SELECT_KITCHEN_ISLAND_SAME_AS_WORKTOP, SELECT_KITCHEN_ISLAND_SPLASH_BACK,
  REVERT_PLAN,
  SELECT_APPOINTMENT_BOOKING_CARD,
} from 'domain/Options/constants';
import {
  NO_WORKTOP_MATERIAL
} from 'domain/Options/generators/materials';

import { applicationModeSelector } from 'Application/selectors';
import { KIOSK } from 'Application/constants';
import { itemListSelector, totalPriceSelector } from 'domain/Options/itemList';
import {
  cutoutsSelector, freestandingHobsSelector, singleCutsSelector, doubleCutsSelector, tripleCutsSelector, roundedCornersSelector,
  wallPanelRoundCutsSmallSelector, wallPanelRoundCutsBigSelector, wallPanelSquareCutsSelector, wallPanelSingleCutsSelector, wallPanelDoubleCutsSelector, wallPanelTripleCutsSelector,
  invertedCornersSelector, tapHolesSelector, kitchenIslandSingleCutsSelector, kitchenIslandRoundedCornersSelector, kitchenIslandSquareCutsSelector
} from 'domain/Options/selectors/operations';
import { applicationSettingsSelector, localeSelector, dataProviderSettingsSelector, currencySelector } from 'domain/LanguageProvider/selectors';
import { doubleSidedSelector, kitchenIslandDoubleSidedSelector, kitchenIslandSplashbackSelector, plannedShapeParameterSelector, materialParameterSelector } from 'domain/Options/selectors/parameters';
import {
  CookieConsentService
} from "@inter-ikea-kompis/services";
import { IpexMomentEnum, DesignSourceEnum, DesignInteractionEnum } from "@insights/insights-data-provider";
import { InsightsApi } from "@insights/insights-data-provider";

const insightsApi = new InsightsApi();
let isConnected = false;

const WORKTOP = 'Worktop';
const SPLASHBACK = 'Splashback';
const KITCHENISLAND = 'KitchenIsland';
const SINK = 'Sink';


const disconnect = () => {
  if (isConnected) {
    this.insightsApi.disconnect();
    this.isConnected = false;
  }
}

async function connect(dpcSettings) {
  if (isConnected) {
    return;
  }
  let ongoingPromise;
  // Awaits the result of the ongoing promise if there are multiple requests.
  if (ongoingPromise) {
    return await ongoingPromise;
  }
  ongoingPromise = insightsApi.connectApplication(dpcSettings);
  await ongoingPromise;

  isConnected = true;
  ongoingPromise = null;
  return ongoingPromise;
}

function* checkEnvironment() {
  const applicationMode = yield select(applicationModeSelector);
  const isRunningInLocal = window.location.href.includes('local');
  const isRunningInCTEF = window.location.href.includes('cte');
  return (((applicationMode === KIOSK && (!isRunningInCTEF && !isRunningInLocal)) || (!isRunningInCTEF && !isRunningInLocal)));
}

function* checkAnalyticsConsent() {
  const analyticsAllowed = yield checkEnvironment();
  if (!analyticsAllowed) {
    return false;
  }

  const applicationSettings = yield select(applicationSettingsSelector);
  const locale = yield select(localeSelector);
  const cookieLocale = `${locale["language"]}-${(locale["country"]).toUpperCase()}`;
  const hasCookieConsent = yield new CookieConsentService({
    locale: cookieLocale,
    settings: applicationSettings
  }).hasCookieConsent(2);

  console.log("hasCookieConsent is: ", hasCookieConsent);
  if (!hasCookieConsent) {
    disconnect();
    return false;
  }
  return true;
}

export function* sendInitialAnalytics(code) {
  const consentGranted = yield* checkAnalyticsConsent();
  if (consentGranted) {
    const dpcSettings = yield select(dataProviderSettingsSelector);
    yield connect(dpcSettings);
    if (code && code !== '') {
      insightsApi.initPlanningSession(
        code,
        DesignSourceEnum.enterCode
      );
      insightsApi.sendDesignInteractionEvent(
        {
          designId: code,
          interaction: DesignInteractionEnum.getConfiguration,
        }
      );
    } else if (!code || code === '') {
      insightsApi.initPlanningSession(
        "",
        DesignSourceEnum.startFromScratch
      );
    }
  }
}

function* selectAppointmentBookingCard(pageId) {
  const consentGranted = yield* checkAnalyticsConsent();
  if (consentGranted) {
    const dpcSettings = yield select(dataProviderSettingsSelector);
    yield connect(dpcSettings);
    insightsApi.sendEvent({
      ipexMoment: IpexMomentEnum.helpMe,
      event: "remote_planning_card_click",
      payload: {
        type: "remote_planning_card",
        pageId: pageId,
      }
    });
  }
}

function* sendAnalytics(event, itemList, shapeMeasurements, materialParameter) {
  const consentGranted = yield* checkAnalyticsConsent();
  if (consentGranted) {
    const dpcSettings = yield select(dataProviderSettingsSelector);
    yield connect(dpcSettings);
    if (itemList || event.planner_event === 'saved_design') {
      if (itemList) {
        const currencyVersion = yield select(currencySelector);
        const totalPrice = yield select(totalPriceSelector);
        const items = itemList.filter(article => article.amount > 0).map((article) => {
          return {
            id: article.item.ItemNo,
            price: article.item.price,
            qty: article.amount,
          };
        });
        insightsApi.sendEvent({
          ipexMoment: IpexMomentEnum.giveMe,
          event: "shopping_bag",
          payload: {
            items: items,
            totalPrice: totalPrice,
            currency: currencyVersion
          }
        });
      }
      if (shapeMeasurements && materialParameter.value.id !== NO_WORKTOP_MATERIAL) {
        insightsApi.sendEvent({
          ipexMoment: IpexMomentEnum.makeItMe,
          event: "measurements",
          payload: {
            type: "worktop_measurements",
            measurements: shapeMeasurements.measurements,
            id: shapeMeasurements.value.id,
            category: "Worktop"
          }
        });
      }
      if (event.planner_event === 'saved_design') {
        insightsApi.saveDesign(
          event.product_combination// VPC code
        );
        insightsApi.sendDesignInteractionEvent(event.product_combination, DesignInteractionEnum.storeConfiguration);
      }
    }
  }
}


function* sendCartUpdateAnalytics(typeOfEvent, eventValue, category, isHolesOrCuts) {
  const consentGranted = yield* checkAnalyticsConsent();
  if (consentGranted) {
    const dpcSettings = yield select(dataProviderSettingsSelector);
    yield connect(dpcSettings);
    let eventOption = "select_option";
    if(isHolesOrCuts){
      eventOption = "add_option";
    }
    insightsApi.sendEvent({
      ipexMoment: IpexMomentEnum.makeItMe, 
      event: eventOption,
      payload: {
        type: typeOfEvent,
        value: eventValue,
        category: category
      }
    });
  }
}

function* buttonsAnalytics(event) {
  const consentGranted = yield* checkAnalyticsConsent();
  if (consentGranted) {
    const dpcSettings = yield select(dataProviderSettingsSelector);
    yield connect(dpcSettings);
    insightsApi.sendEvent({
      ipexMoment: IpexMomentEnum.giveMe, 
      event: event.planner_event,
      payload: {
        type: event.selectButton,
      }
    });
  }
}

export function* sendShowItemList() {
  const itemList = (yield select(itemListSelector)).toJS();
  const shapeMeasurements = (yield select(plannedShapeParameterSelector)).toJS();
  const materialParameter = (yield select(materialParameterSelector)).toJS();
  yield sendAnalytics({
    planner_event: 'view_products',
  }, itemList, shapeMeasurements, materialParameter);
}

export function* sendSavedDesign(itemList, code) {
  yield sendAnalytics({
    planner_event: 'saved_design',
    product_combination: code,
  });
}

export function* sendSelectedPrintSummary() {
  yield buttonsAnalytics({
    planner_event: 'print_summary',
    Button: 'printsummary'
  });
}

export function* sendSelectedOrderWorktopOnline() {
  yield buttonsAnalytics({
    planner_event: 'order_worktop_online',
    Button: 'orderworktoponline'
  });
}

export function* sendCopyLink() {
  yield buttonsAnalytics({
    planner_event: 'send_copy_link',
    Button: 'sendcopylink'
  });
}

function* selectMaterial(action) {
  yield sendCartUpdateAnalytics("material", action.materialId, WORKTOP);
}
function* selectKitchenIslandMaterial(action) {
  yield sendCartUpdateAnalytics("material", action.materialId, KITCHENISLAND);
}
function* selectWallPanelMaterial(action) {
  yield sendCartUpdateAnalytics("wallpanel_material", action.materialId, SPLASHBACK);
}

function* selectExpression(action) {
  yield sendCartUpdateAnalytics("expression", action.expressionId, WORKTOP);
}
function* selectKitchenIslandExpression(action) {
  yield sendCartUpdateAnalytics("expression", action.expressionId, KITCHENISLAND);
}
function* selectWallPanelExpression(action) {
  yield sendCartUpdateAnalytics("wallpanel_expression", action.expressionId, SPLASHBACK);
}

function* selectEdge(action) {
  yield sendCartUpdateAnalytics("edge", action.edgeId, WORKTOP);
}
function* selectKitchenIslandEdge(action) {
  yield sendCartUpdateAnalytics("edge", action.edgeId, KITCHENISLAND);
}

function* selectThickness(action) {
  yield sendCartUpdateAnalytics("thickness", action.thicknessId, WORKTOP);
}
function* selectKitchenIslandThickness(action) {
  yield sendCartUpdateAnalytics("thickness", action.thicknessId, KITCHENISLAND);
}

function* selectSplashback(action) {
  yield sendCartUpdateAnalytics("splashback", action.splashbackId, SPLASHBACK);
}
function* selectWallPanelMatchWorktop(action) {
  yield sendCartUpdateAnalytics("wallpanel_match_worktop", action.matchWorktop, SPLASHBACK);
}
function* selectKitchenIslandWallPanelMatchWorktop() {
  const kitchenIslandSplashback = yield select(kitchenIslandSplashbackSelector);
  yield sendCartUpdateAnalytics("wallpanel_match_worktop", kitchenIslandSplashback, KITCHENISLAND);
}
function* selectKitchenIslandSameAsWorktop(action) {
  yield sendCartUpdateAnalytics("same_as_worktop", action.sameAsWorktop, KITCHENISLAND);
}

function* selectDoubleSided() {
  const doubleSided = yield select(doubleSidedSelector);
  yield sendCartUpdateAnalytics("double_sided", doubleSided, WORKTOP);
}
function* selectKitchenIslandDoubleSided() {
  const doubleSided = yield select(kitchenIslandDoubleSidedSelector);
  yield sendCartUpdateAnalytics("double_sided", doubleSided, KITCHENISLAND);
}

function* selectWallLocation(action) {
  yield sendCartUpdateAnalytics("wall_location", action.wallLocationId, WORKTOP);
}
function* selectKitchenIslandWallLocation(action) {
  yield sendCartUpdateAnalytics("wall_location", action.wallLocationId, KITCHENISLAND);
}

function* selectOverhang(action) {
  yield sendCartUpdateAnalytics("overhang", action.overhangId, WORKTOP);
}
function* selectKitchenIslandOverhang(action) {
  yield sendCartUpdateAnalytics("overhang", action.overhangId, KITCHENISLAND);
}

function* selectOverhangSide(action) {
  yield sendCartUpdateAnalytics("overhang_side", action.sideId, WORKTOP);
}
function* selectKitchenIslandOverhangSide(action) {
  yield sendCartUpdateAnalytics("overhang_side", action.overhangSideId, KITCHENISLAND);
}

function* selectSinkMaterial(action) {
  yield sendCartUpdateAnalytics("sink_material", action.sinkMaterialId, SINK);
}
function* selectSinkModel(action) {
  yield sendCartUpdateAnalytics("sink_model", action.sinkModelId, SINK);
}
function* selectSinkFastening(action) {
  yield sendCartUpdateAnalytics("sink_fastening", action.sinkFasteningId, SINK);
}
function* selectDrainerGrooves(action) {
  yield sendCartUpdateAnalytics("drainer_grooves", action.drainerGroovesId, SINK);
}

function* selectRevertPlan() {
  yield sendCartUpdateAnalytics("revert_plan", true);
}

function* selectNrOfHolesOrCuts(type) {
  const [filterSelected, amount, category] = yield getHolesOrCutsValues(type);
  if (filterSelected && amount) {
    yield sendCartUpdateAnalytics(filterSelected.toLowerCase(), amount, category, "isHolesOrCuts");
  } else {
    console.log("selectNrOfHolesOrCuts analytics not possible - error: ");
  }
}

/* eslint-disable no-constant-condition */
function* selectShapeSaga(action) {
  if(!action.changedMeasurement){
    yield sendCartUpdateAnalytics("shape", action.shapeId, WORKTOP);
  }
}

function getValues(action) {
  switch (action.payload.pathname) {
    case "/compare":
      return ["compare_plans", 1];
    case "/main/style":
      return ["styling", 1];
    case "/main/plan":
      return ["shape", 2];
    case "/main/sink":
      return ["sink", 3];
    case "/main/cutouts":
      return ["cutouts", 4];
    case "/main/kitchen-island":
      return ["kitchen_island", 5];
    case "/itemlist":
      return ["checkout", 6];
    case "":
      return ["start_page", 0];
    default:
      return ["start_page", 0];
  }
}
function* getHolesOrCutsValues(holeOrCut) {
  const type = holeOrCut['type'];
  switch (type) {
    case CHANGE_CUTOUTS: // Used for Hob cutouts
      const hobCutOut = yield select(cutoutsSelector);
      return ["hobCutouts", hobCutOut.get('value'), WORKTOP]
    case CHANGE_TAP_HOLES:
      const tapHoles = yield select(tapHolesSelector);
      return ["tapHoles", tapHoles.get('value'), SINK]
    case CHANGE_ROUNDED_CORNERS:
      const roundedCorners = yield select(roundedCornersSelector);
      return ["rounded_corners", roundedCorners.get('value'), WORKTOP]
    case CHANGE_INVERTED_CORNERS:
      const invertedCorners = yield select(invertedCornersSelector);
      return ["inverted_corners", invertedCorners.get('value'), WORKTOP]
    case CHANGE_SINGLE_CUTS:
      const singleCuts = yield select(singleCutsSelector);
      return ["single_cuts", singleCuts.get('value'), WORKTOP]
    case CHANGE_DOUBLE_CUTS:
      const doubleCuts = yield select(doubleCutsSelector);
      return ["double_cuts", doubleCuts.get('value'), WORKTOP]
    case CHANGE_TRIPLE_CUTS:
      const tripleCuts = yield select(tripleCutsSelector);
      return ["triple_cuts", tripleCuts.get('value'), WORKTOP]
    case CHANGE_WALL_PANEL_ROUND_CUTS_SMALL:
      const wallPanelRoundCutsSmall = yield select(wallPanelRoundCutsSmallSelector);
      return ["wallpanel_roundcuts_small", wallPanelRoundCutsSmall.get('value'), SPLASHBACK]
    case CHANGE_WALL_PANEL_ROUND_CUTS_BIG:
      const wallPanelRoundCutsBig = yield select(wallPanelRoundCutsBigSelector);
      return ["Wallpanel_roundcuts_big", wallPanelRoundCutsBig.get('value'), SPLASHBACK]
    case CHANGE_WALL_PANEL_SQUARE_CUTS:
      const wallPanelSquareCuts = yield select(wallPanelSquareCutsSelector);
      return ["wallpanel_squarecuts", wallPanelSquareCuts.get('value'), SPLASHBACK]
    case CHANGE_WALL_PANEL_SINGLE_CUTS:
      const wallPanelSingleCuts = yield select(wallPanelSingleCutsSelector);
      return ["wallpanel_singlecuts", wallPanelSingleCuts.get('value'), SPLASHBACK]
    case CHANGE_WALL_PANEL_DOUBLE_CUTS:
      const wallPanelDoubleCuts = yield select(wallPanelDoubleCutsSelector);
      return ["wallpanel_doubleCuts", wallPanelDoubleCuts.get('value'), SPLASHBACK]
    case CHANGE_WALL_PANEL_TRIPLE_CUTS:
      const wallPanelTripleCuts = yield select(wallPanelTripleCutsSelector);
      return ["wallpanel_triplecuts", wallPanelTripleCuts.get('value'), SPLASHBACK]
    case CHANGE_FREESTANDING_HOBS:
      const freeStandingHobs = yield select(freestandingHobsSelector);
      return ["free_standing_hobs", freeStandingHobs.get('value'), WORKTOP]
    case CHANGE_KITCHEN_ISLAND_SINGLE_CUTS:
      const kitchenIslandSingleCuts = yield select(kitchenIslandSingleCutsSelector);
      return ["single_cuts", kitchenIslandSingleCuts.get('value'), KITCHENISLAND]
    case CHANGE_KITCHEN_ISLAND_ROUNDED_CORNERS:
      const kitchenIslandRoundedCorners = yield select(kitchenIslandRoundedCornersSelector);
      return ["rounded_corners", kitchenIslandRoundedCorners.get('value'), KITCHENISLAND]
    case CHANGE_KITCHEN_ISLAND_SQUARE_CUTS:
      const kitchenIslandSquareCuts = yield select(kitchenIslandSquareCutsSelector);
      return ["square_cuts", kitchenIslandSquareCuts.get('value'), KITCHENISLAND]
    default:
      return [];
  }
}



function* sendLocationAnalytics(action) {
  const consentGranted = yield* checkAnalyticsConsent();
  if (consentGranted) {
    const dpcSettings = yield select(dataProviderSettingsSelector);
    yield connect(dpcSettings);
    const [plannerPart, plannerStep] = getValues(action);

    if(plannerPart === "checkout"){
      yield sendShowItemList();
    }
    insightsApi.sendUserFlowStep(
      'user_planning_flow', // flow name
      plannerPart, // step name
      plannerStep // step level
    );
  }
}

// All sagas to be loaded
export default function* () {
  yield all([
    takeLatest(LOCATION_CHANGE, sendLocationAnalytics),

    takeEvery(SELECT_MATERIAL, selectMaterial),
    takeEvery(SELECT_KITCHEN_ISLAND_MATERIAL, selectKitchenIslandMaterial),
    takeEvery(SELECT_WALL_PANEL_MATERIAL, selectWallPanelMaterial),

    takeEvery(SET_DOUBLE_SIDED, selectDoubleSided),
    takeEvery(SET_KITCHEN_ISLAND_DOUBLE_SIDED, selectKitchenIslandDoubleSided),

    takeEvery(SELECT_EXPRESSION, selectExpression),
    takeEvery(SELECT_KITCHEN_ISLAND_EXPRESSION, selectKitchenIslandExpression),
    takeEvery(SELECT_WALL_PANEL_EXPRESSION, selectWallPanelExpression),

    takeEvery(SELECT_EDGE, selectEdge),
    takeEvery(SELECT_KITCHEN_ISLAND_EDGE, selectKitchenIslandEdge),

    takeEvery(SELECT_THICKNESS, selectThickness),
    takeEvery(SELECT_KITCHEN_ISLAND_THICKNESS, selectKitchenIslandThickness),

    takeEvery(SELECT_WALL_LOCATION, selectWallLocation),
    takeEvery(SELECT_KITCHEN_ISLAND_WALL_LOCATION, selectKitchenIslandWallLocation),

    takeEvery(SELECT_SPLASHBACK, selectSplashback),
    takeEvery(SELECT_WALL_PANEL_MATCH_WORKTOP, selectWallPanelMatchWorktop),
    takeEvery(SELECT_KITCHEN_ISLAND_SPLASH_BACK, selectKitchenIslandWallPanelMatchWorktop),
    takeEvery(SELECT_KITCHEN_ISLAND_SAME_AS_WORKTOP, selectKitchenIslandSameAsWorktop),

    takeEvery(CHANGE_SHAPE, selectShapeSaga),

    takeEvery(SELECT_SINK_MODEL, selectSinkModel),
    takeEvery(SELECT_SINK_MATERIAL, selectSinkMaterial),
    takeEvery(SELECT_SINK_FASTENING, selectSinkFastening),
    takeEvery(SELECT_DRAINER_GROOVES, selectDrainerGrooves),

    takeEvery(SELECT_OVERHANG, selectOverhang),
    takeEvery(SELECT_KITCHEN_ISLAND_OVERHANG, selectKitchenIslandOverhang),

    takeEvery(SELECT_OVERHANG_SIDE, selectOverhangSide),
    takeEvery(SELECT_KITCHEN_ISLAND_OVERHANG_SIDE, selectKitchenIslandOverhangSide),

    takeEvery(REVERT_PLAN, selectRevertPlan),

    takeEvery([CHANGE_TAP_HOLES, CHANGE_CUTOUTS, CHANGE_SINGLE_CUTS, CHANGE_DOUBLE_CUTS, CHANGE_TRIPLE_CUTS, CHANGE_ROUNDED_CORNERS, CHANGE_INVERTED_CORNERS,
      CHANGE_WALL_PANEL_ROUND_CUTS_SMALL, CHANGE_WALL_PANEL_ROUND_CUTS_BIG,
      CHANGE_WALL_PANEL_SQUARE_CUTS, CHANGE_WALL_PANEL_SINGLE_CUTS, CHANGE_WALL_PANEL_DOUBLE_CUTS, CHANGE_WALL_PANEL_TRIPLE_CUTS,
      CHANGE_FREESTANDING_HOBS, CHANGE_KITCHEN_ISLAND_SINGLE_CUTS, CHANGE_KITCHEN_ISLAND_ROUNDED_CORNERS, CHANGE_KITCHEN_ISLAND_SQUARE_CUTS,
    ], selectNrOfHolesOrCuts),

    takeEvery(SELECT_APPOINTMENT_BOOKING_CARD, selectAppointmentBookingCard),
  ]);

}
